import { createContext, createElement, useContext, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";

const CheckoutCtx = createContext(() => {});
export const CheckoutProvider = CheckoutCtx.Provider;
export const useCheckout = () => useContext(CheckoutCtx);

const EASE = [0.16, 1, 0.3, 1];

export function MaskedLines({
  lines,
  as = "h2",
  className = "",
  delay = 0,
  stagger = 0.12,
  inView = false,
}) {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-60px" });
  const active = inView ? seen : true;
  const content = lines.map((line, i) => (
    <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: 90 }}
        animate={active ? { y: 0 } : { y: 90 }}
        transition={{ duration: 0.95, ease: EASE, delay: delay + i * stagger }}
      >
        {line}
      </motion.span>
    </span>
  ));
  return createElement(as, { className, ref }, content);
}

export function Reveal({ children, delay = 0, y = 36, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.85, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export function Overline({ children, className = "" }) {
  return (
    <p className={`font-dmsans text-[11px] font-semibold uppercase tracking-[0.3em] text-hc-lilac ${className}`}>
      {children}
    </p>
  );
}

const ctaVariants = {
  white: "bg-white text-black hover:bg-white/85",
  black: "bg-black text-white hover:bg-black/80",
  gradient:
    "bg-gradient-to-r from-mom-vinho to-mom-carmim text-white shadow-[0_10px_40px_rgba(169,21,50,0.35)] hover:brightness-110",
};

export function CtaButton({ children, testId, onClick, variant = "white", className = "" }) {
  const openCheckout = useCheckout();
  return (
    <motion.button
      data-testid={testId}
      onClick={onClick || openCheckout}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className={`group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-dmsans text-xs font-semibold uppercase tracking-[0.14em] transition-[filter,background-color,transform] duration-300 sm:text-sm ${ctaVariants[variant]} ${className}`}
    >
      <span>{children}</span>
      <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
    </motion.button>
  );
}

export function Section({ id, tone = "black", children, className = "" }) {
  const tones = {
    black: "bg-black text-white",
    white: "bg-mom-cream text-hc-ink",
    paper: "bg-white text-hc-ink",
  };
  return (
    <section id={id} className={`${tones[tone]} ${className}`}>
      <div className="mx-auto w-full max-w-6xl px-6 py-24 sm:px-10 lg:py-32">
        {children}
      </div>
    </section>
  );
}
